import { Box, Button, FormHelperText, Stack, Typography } from "@mui/material";
import { SettingsWrapper } from "../SettingsWrapper";
import { PaymentMethod } from "../PaymentMethod";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { fetchPaymentMethods } from "../../../../features/paymentMethods/paymentMethods.thunk";
import { PaymentMethodSkeleton } from "../PaymentMethodSkeleton";
import { useGetPaymentMethodLink } from "../../../../hooks/useGetPaymentMethodLink";
import { selectExtension } from "../../../../features/extension/extension.selector";
import { keyframes } from "@mui/material/styles";
import { selectUserProfile } from "../../../../features/user/user.selector";
import { BORDER_COLORS, FONT_COLORS } from "../../../../constants";
import AppleIconDesktop from "../../../../assets/apple_billing.svg";
import GooglePlayBilling from "../../../../assets/google_play_billing.svg";
import { isStoreManagedSubscription } from "../../../../helpers";

const pulseRectangle = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.7);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(211, 47, 47, 0.4);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(211, 47, 47, 0);
  }
`;

export const PaymentDetails = () => {
  const { isLoading, data, error } = useAppSelector(
    (state) => state.paymentMethods,
  );
  const { isExtension } = useAppSelector(selectExtension);

  const dispatch = useAppDispatch();

  const {
    getLink,
    error: getLinkError,
    loading: getLinkLoading,
  } = useGetPaymentMethodLink({
    onSuccess: (link) => {
      window.open(link, isExtension ? "_blank" : "_self");
    },
  });
  const user = useAppSelector(selectUserProfile);
  const subscriptionState = user?.subscriptionState;
  const isStoreManaged = isStoreManagedSubscription(user?.store);
  const storeCopy =
    user?.store === "PLAY_STORE"
      ? {
          icon: GooglePlayBilling,
          alt: "Google Play",
          title: "Your subscription is managed through Google Play.",
          subtitle:
            "To manage your subscription or payment method, please use your Android device.",
        }
      : {
          icon: AppleIconDesktop,
          alt: "App Store",
          title: "Your subscription is managed through the App Store.",
          subtitle:
            "To manage your subscription or payment method, please use your Apple device.",
        };

  useEffect(() => {
    if (isStoreManaged) return;
    const promise = dispatch(fetchPaymentMethods());
    return () => {
      promise.abort();
    };
  }, [dispatch, isStoreManaged]);

  if (isStoreManaged) {
    return (
      <SettingsWrapper
        heading="Payment Details"
        subHeading="Select default payment method or add a new one"
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={2}
          px={2}
          py={1.5}
          border={`1px solid #E0E0E0`}
          borderRadius={1}
          sx={{ backgroundColor: "#FFFFFF66" }}
        >
          <Box
            component="img"
            src={storeCopy.icon}
            alt={storeCopy.alt}
            sx={{ width: 48, height: 48, flexShrink: 0 }}
          />
          <Stack gap={0.5}>
            <Typography
              fontSize={14}
              fontWeight={600}
              color="#111827"
            >
              {storeCopy.title}
            </Typography>
            <Typography
              fontSize={13}
              fontWeight={400}
              color="#6B7280"
            >
              {storeCopy.subtitle}
            </Typography>
          </Stack>
        </Stack>
      </SettingsWrapper>
    );
  }

  return (
    <SettingsWrapper
      heading="Payment Details"
      subHeading="Select default payment method or add a new one"
      renderButton={(props) => {
        const paymentButton = (
          <Button {...props} onClick={getLink} loading={getLinkLoading}>
            {!data.length ? "Set Up Payment Method" : "Update Payment Method"}
          </Button>
        );

        return (
          <Box>
            {paymentButton}
            {getLinkError && (
              <FormHelperText error>{getLinkError}</FormHelperText>
            )}
          </Box>
        );
      }}
    >
      {isLoading ? (
        <PaymentMethodSkeleton />
      ) : !!error ? (
        <FormHelperText error>Failed to show payment methods</FormHelperText>
      ) : !!data.length ? (
        <PaymentMethod key={data[0].id} paymentMethod={data[0]} />
      ) : (
        <FormHelperText>
          You haven't connected any payment method yet
        </FormHelperText>
      )}
    </SettingsWrapper>
  );
};
